import React from 'react';

import { link } from '@aline/routes';
import { alpha, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AlineTooltip } from 'src/components/generic';
import type { StorableIconDocument } from 'src/components/StorableIcon';
import { StorableIcon } from 'src/components/StorableIcon';
import { openLink } from 'src/helpers';
import { useGetDocument } from 'src/hooks';
import type { WorkflowFileSubset } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    position: 'relative',
    width: '100%',
  },
  fileChip: {
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.05),
    },
    alignItems: 'center',
    border: theme.appBorders.outline,
    borderRadius: 8,
    boxSizing: 'border-box',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    display: 'flex',
    gap: theme.spacing(0.5),
    maxWidth: '100%',
    overflow: 'hidden',
    padding: theme.spacing(0.25, 0.75),
    width: 'fit-content',
  },
  fileChipText: {
    fontSize: theme.typography.pxToRem(12),
    maxWidth: 120,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  filesGrid: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  remainingCircle: {
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.2),
    },
    alignItems: 'center',
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    borderRadius: '50%',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    display: 'flex',
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 600,
    height: 24,
    justifyContent: 'center',
    minWidth: 24,
    transition: 'background-color 0.2s',
  },
  searchButton: {
    bottom: 0,
    position: 'absolute',
    right: 0,
  },
}));

interface StorableChipProps {
  storable: StorableIconDocument & { name: string | null };
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}

/** Compact chip showing the storable icon, truncated title, and a tooltip with the full name. */
export function StorableChip({
  className,
  onClick,
  onKeyDown,
  storable,
}: StorableChipProps): JSX.Element {
  const { classes } = useStyles();
  const name = storable.name ?? 'Untitled';

  return (
    <AlineTooltip arrow placement="top" title={name}>
      <div
        className={className ?? classes.fileChip}
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={onKeyDown}
      >
        <StorableIcon iconProps={{ height: 16, width: 16 }} storable={storable} />
        <Typography noWrap className={classes.fileChipText} color="primary">
          {name}
        </Typography>
      </div>
    </AlineTooltip>
  );
}

interface DocumentChipByIdProps {
  documentId: UUID;
  onDelete?: () => void;
}

/** Fetches a document by ID and renders a StorableChip. */
export function DocumentChipById({ documentId, onDelete }: DocumentChipByIdProps) {
  const { document } = useGetDocument(documentId);
  if (!document) return null;
  return <StorableChip storable={document} onClick={onDelete} />;
}

interface WorkflowFileChipsProps {
  files: WorkflowFileSubset[];
  workflowId: UUID;
  maxDisplayed?: number;
}

export function WorkflowFileChips({
  files,
  maxDisplayed = 3,
  workflowId,
}: WorkflowFileChipsProps): JSX.Element | null {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const displayedFiles = files.slice(0, maxDisplayed);
  const remainingCount = Math.max(0, files.length - maxDisplayed);

  const handleFileClick = (e: React.MouseEvent, documentId: UUID) => {
    e.stopPropagation();
    openLink(link.DocumentDetail({ documentId }));
  };

  return (
    <div className={classes.container}>
      <div className={classes.filesGrid}>
        {displayedFiles.map((file, index) => (
          <StorableChip
            key={index}
            storable={file.document}
            onClick={e => handleFileClick(e, file.document.id)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ')
                openLink(link.DocumentDetail({ documentId: file.document.id }));
            }}
          />
        ))}
        {remainingCount > 0 && (
          <div
            className={classes.remainingCircle}
            onClick={e => {
              e.stopPropagation();
              navigate(link.WorkflowDetailTabDocuments({ workflowId }));
            }}
          >
            +{remainingCount}
          </div>
        )}
      </div>
    </div>
  );
}
